import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ColorInput,
  Drawer,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import type { CategoryDto } from './types';
import { useCategories, useCreateCategory, useUpdateCategory } from './categoriesApi';
import { useBranches } from '../branches/branchesApi';
import { useAllKitchenStations } from '../kitchen-stations/useAllKitchenStations';

interface Props {
  opened: boolean;
  onClose: () => void;
  category: CategoryDto | null;
}

interface FormValues {
  nameAr: string;
  nameEn: string;
  parentCategoryId: string | null;
  sortOrder: number;
  isActive: boolean;
  colorHex: string;
  iconName: string;
  kitchenStationId: string | null;
}

export function CategoryFormDrawer({ opened, onClose, category }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreateCategory();
  const update = useUpdateCategory();
  const allCategories = useCategories();
  const branchesQuery = useBranches();
  const stationsQuery = useAllKitchenStations(branchesQuery.data ?? []);
  const submitting = create.isPending || update.isPending;
  const editing = category !== null;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: {
      nameAr: '',
      nameEn: '',
      parentCategoryId: null,
      sortOrder: 0,
      isActive: true,
      colorHex: '',
      iconName: '',
      kitchenStationId: null,
    },
    validate: {
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      nameAr: category?.nameAr ?? '',
      nameEn: category?.nameEn ?? '',
      parentCategoryId: category?.parentCategoryId ?? null,
      sortOrder: category?.sortOrder ?? 0,
      isActive: category?.isActive ?? true,
      colorHex: category?.colorHex ?? '',
      iconName: category?.iconName ?? '',
      kitchenStationId: category?.kitchenStationId ?? null,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, category]);

  // Parent picker — exclude self (a category can't be its own parent) and
  // anything that would create a cycle (descendants of self).
  const parentOptions = useMemo(() => {
    const all = allCategories.data ?? [];
    const disallowed = editing ? collectDescendantIds(all, category!.categoryId) : new Set<string>();
    return all
      .filter((c) => !disallowed.has(c.categoryId))
      .map((c) => ({
        value: c.categoryId,
        label: i18n.language === 'ar' ? c.nameAr : (c.nameEn ?? c.nameAr),
      }));
  }, [allCategories.data, category, editing, i18n.language]);

  // KDS station picker — categories aren't branch-scoped but stations are,
  // so prefix each option with the branch name. KitchenService routing
  // falls back to the branch default when the mapped station belongs to
  // a different branch than the order, so multi-branch tenants get sane
  // behavior even when the same category maps once.
  const stationOptions = useMemo(() => {
    const branches = branchesQuery.data ?? [];
    const stations = stationsQuery.data ?? [];
    const branchName = (id: string) => {
      const b = branches.find((x) => x.branchId === id);
      if (!b) return '';
      return i18n.language === 'ar' ? b.nameAr : (b.nameEn ?? b.nameAr);
    };
    const showBranchPrefix = branches.length > 1;
    return stations
      .filter((s) => s.isActive && !s.isDefault)
      .map((s) => {
        const stationName = i18n.language === 'ar' ? s.nameAr : (s.nameEn ?? s.nameAr);
        const label = showBranchPrefix
          ? `${branchName(s.branchId)} — ${stationName}`
          : stationName;
        return { value: s.kitchenStationId, label };
      });
  }, [branchesQuery.data, stationsQuery.data, i18n.language]);

  const submit = async (values: FormValues) => {
    const payload = {
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
      parentCategoryId: values.parentCategoryId,
      sortOrder: values.sortOrder,
      colorHex: values.colorHex.trim() || null,
      iconName: values.iconName.trim() || null,
      kitchenStationId: values.kitchenStationId,
    };
    try {
      if (editing) {
        await update.mutateAsync({
          id: category.categoryId,
          request: { ...payload, isActive: values.isActive },
        });
      } else {
        await create.mutateAsync(payload);
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: payload.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('categories.form.editTitle') : t('categories.form.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <TextInput
            label={t('categories.form.nameArLabel')}
            withAsterisk
            placeholder={t('categories.form.nameArPlaceholder')}
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('categories.form.nameEnLabel')}
            placeholder={t('categories.form.nameEnPlaceholder')}
            {...form.getInputProps('nameEn')}
          />
          <Select
            label={t('categories.form.parentLabel')}
            description={t('categories.form.parentHelp')}
            placeholder={t('categories.form.parentPlaceholder')}
            data={parentOptions}
            value={form.values.parentCategoryId}
            onChange={(value) => form.setFieldValue('parentCategoryId', value)}
            clearable
            searchable
            disabled={allCategories.isLoading}
          />
          <Group grow>
            <NumberInput
              label={t('categories.form.sortOrderLabel')}
              description={t('categories.form.sortOrderHelp')}
              min={0}
              {...form.getInputProps('sortOrder')}
            />
            <ColorInput
              label={t('categories.form.colorLabel')}
              format="hex"
              swatches={[
                '#B5191D', '#DC2626', '#F59E0B', '#10B981',
                '#0EA5E9', '#6366F1', '#8B5CF6', '#64748B',
              ]}
              value={form.values.colorHex}
              onChange={(value) => form.setFieldValue('colorHex', value)}
            />
          </Group>
          <Select
            label={t('categories.form.stationLabel')}
            description={t('categories.form.stationHelp')}
            placeholder={t('categories.form.stationPlaceholder')}
            data={stationOptions}
            value={form.values.kitchenStationId}
            onChange={(value) => form.setFieldValue('kitchenStationId', value)}
            clearable
            searchable
            disabled={stationsQuery.isLoading || branchesQuery.isLoading}
          />
          {editing && (
            <Switch
              label={t('categories.form.activeLabel')}
              description={t('categories.form.activeHelp')}
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

// Walk the category tree from `rootId` and collect every descendant + self,
// so the parent picker can't accept a cycle (own ancestor of the category
// being edited).
function collectDescendantIds(all: CategoryDto[], rootId: string): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  all.forEach((c) => {
    if (!c.parentCategoryId) return;
    const list = childrenByParent.get(c.parentCategoryId) ?? [];
    list.push(c.categoryId);
    childrenByParent.set(c.parentCategoryId, list);
  });

  const result = new Set<string>([rootId]);
  const queue = [rootId];
  while (queue.length > 0) {
    const id = queue.shift()!;
    for (const child of childrenByParent.get(id) ?? []) {
      if (!result.has(child)) {
        result.add(child);
        queue.push(child);
      }
    }
  }
  return result;
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
